import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Badge,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MedicationIcon from "@mui/icons-material/Medication";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScienceIcon from "@mui/icons-material/Science";
import FileOpenIcon from '@mui/icons-material/FileOpen';
import GridViewIcon from "@mui/icons-material/GridView";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { IoMdArrowDropdown } from "react-icons/io";
import { useIpdData } from "../../context/IpdDataContext";
import { fetchDischargeProcess, STAGE_LABELS } from "../../api/dischargeProcessApi";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(3),
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#fff",
}));

const SummaryCard = styled(Card)(({ theme, color }) => ({
  borderRadius: 12,
  backgroundColor: color || theme.palette.background.paper,
  color: "#fff",
  minHeight: 100,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  borderRadius: 20,
  textTransform: "none",
  padding: theme.spacing(1, 2),
  backgroundColor: active ? "#4caf50" : "#f5f5f5",
  color: active ? "#fff" : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: active ? "#45a049" : "#e0e0e0",
  },
}));

const TaskCard = styled(Paper)(({ theme, urgent, completed }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  borderRadius: 12,
  border: `2px solid ${urgent ? "#ff9800" : "#e0e0e0"}`,
  backgroundColor: completed ? theme.palette.grey[50] : "#fff",
  opacity: completed ? 0.7 : 1,
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
  },
}));

const NurseTasks = () => {
  const { getNurseTasks, updateTaskStatus } = useIpdData();
  const [tasks, setTasks] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedDateStr, setSelectedDateStr] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [loading, setLoading] = useState(true);
  // Read-only DischargeProcess state per admission_id, for the "Discharge
  // Advice" category's status chip (fetched from the real discharge-process
  // API — see fetchDischargeProcess).
  const [dischargeProcessByAdmission, setDischargeProcessByAdmission] = useState({});

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await getNurseTasks();
      if (response?.status === 200 && Array.isArray(response?.data)) {
        setTasks(response.data);
      }
    } catch (error) {
      console.error("Error loading tasks:", error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const resolveAdmissionId = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      const direct = Number(trimmed);
      if (Number.isFinite(direct)) return direct;
      const digits = trimmed.match(/\d+/);
      return digits ? Number(digits[0]) : 0;
    }
    return 0;
  };

  // Batch-fetch the real DischargeProcess state for every admission with a
  // "Discharge Advice" task, whenever the task list (re)loads.
  useEffect(() => {
    const admissionIds = Array.from(
      new Set(
        tasks
          .filter((task) => task.category === "Discharge Advice")
          .map((task) => resolveAdmissionId(task?.admissionId))
          .filter(Boolean)
      )
    );
    if (admissionIds.length === 0) return;

    let cancelled = false;
    Promise.all(
      admissionIds.map((admissionId) =>
        fetchDischargeProcess(admissionId)
          .then((res) => [admissionId, res?.data])
          .catch(() => [admissionId, undefined])
      )
    ).then((results) => {
      if (cancelled) return;
      setDischargeProcessByAdmission((prev) => {
        const next = { ...prev };
        results.forEach(([admissionId, data]) => {
          next[admissionId] = data;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [tasks]);

  const getDischargeStageLabel = (task) => {
    const admissionId = resolveAdmissionId(task?.admissionId);
    const process = dischargeProcessByAdmission[admissionId];
    if (!process || !process.stage || process.stage === "cancelled") {
      return "Not initiated";
    }
    return STAGE_LABELS[process.stage] || process.stage;
  };

  const handleCompleteTask = async (task) => {
    if (!task) return;
    const templateId = task.templateId ?? task.id;
    const scheduledDateTime =
      task.scheduledDateTime ||
      (task.dueDate && task.dueTime
        ? `${task.dueDate}T${String(task.dueTime).slice(0, 5)}:00.000Z`
        : task.dueDate
          ? `${task.dueDate}T00:00:00.000Z`
          : null);
    if (!templateId || !scheduledDateTime) {
      console.warn("Cannot update task: missing templateId or scheduledDateTime", task);
      return;
    }
    try {
      const newStatus = task.status === "Completed" ? "Pending" : "Completed";
      await updateTaskStatus(templateId, newStatus, scheduledDateTime);
      await loadTasks();
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  const getLocalDateKey = (dateValue = new Date()) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateToLocalKey = (rawValue) => {
    if (!rawValue) return "";
    const text = String(rawValue).trim();
    if (!text) return "";
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return getLocalDateKey(parsed);
    }
    const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
    const dmy = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    return "";
  };

  const getTaskDateKey = (task) => {
    const rawDateTime = task?.scheduledDateTime;
    if (rawDateTime) {
      const parsed = new Date(rawDateTime);
      if (!Number.isNaN(parsed.getTime())) {
        return getLocalDateKey(parsed);
      }
      if (typeof rawDateTime === "string" && rawDateTime.includes("T")) {
        return rawDateTime.split("T")[0];
      }
    }
    return parseDateToLocalKey(task?.dueDate);
  };

  // Group tasks by selected date and filter by category
  const tasksByDate = useMemo(() => {
    const grouped = {};
    if (!Array.isArray(tasks)) return grouped;
    tasks.forEach((task) => {
      const compactDetail = String(task.compact_detail || "").trim();
      const compactParts = compactDetail
        ? compactDetail
          .split("|")
          .map((part) => part.trim())
          .filter(Boolean)
        : [];
      const compactTitle = compactParts[0] || "";
      const compactDescription = compactParts.slice(1).join("\n");

      const normalizedTask = {
        ...task,
        taskTitle:
          task.taskTitle || task.task_type || compactTitle || "Nurse Task",
        description:
          task.description || compactDescription || "",
      };

      const taskDate = getTaskDateKey(normalizedTask) || "undated";
      if (selectedDateStr && taskDate !== selectedDateStr && taskDate !== "undated") return;

      // Filter by category
      if (selectedCategory !== "All") {
        const categoryMap = {
          Medicine: "Medicine",
          Vitals: "Vitals",
          Rounds: "Rounds",
          Labs: "Labs",
          "Discharge Advice": "Discharge Advice",
        };
        if (normalizedTask.category !== categoryMap[selectedCategory]) return;
      }

      if (!grouped[taskDate]) {
        grouped[taskDate] = [];
      }
      grouped[taskDate].push(normalizedTask);
    });

    // Sort tasks within each date: pending first, then completed
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        if (a.status === "Completed" && b.status !== "Completed") return 1;
        if (a.status !== "Completed" && b.status === "Completed") return -1;
        // If same status, sort by time
        const timeA = a.dueTime || a.scheduledDateTime?.split("T")[1]?.slice(0, 5) || "";
        const timeB = b.dueTime || b.scheduledDateTime?.split("T")[1]?.slice(0, 5) || "";
        return timeA.localeCompare(timeB);
      });
    });

    return grouped;
  }, [tasks, selectedCategory, selectedDateStr]);

  // Tasks visible in current filter (for summary when date filter active)
  const tasksForSummary = useMemo(
    () => Object.values(tasksByDate).flat(),
    [tasksByDate]
  );

  // Summary (today only)
  const summary = useMemo(() => {
    const source = tasksForSummary;
    const done = source.filter((t) => t.status === "Completed").length;
    const pending = source.filter((t) => t.status === "Pending").length;
    const overdue = source.filter((t) => {
      if (t.status === "Completed") return false;
      const taskTime = t.dueTime || t.scheduledDateTime?.split("T")[1]?.slice(0, 5);
      if (!taskTime) return false;
      const now = new Date();
      const [hours, minutes] = taskTime.split(":").map(Number);
      const taskDateTime = new Date();
      taskDateTime.setHours(hours, minutes, 0, 0);
      return now > taskDateTime;
    }).length;
    return { done, pending, overdue };
  }, [tasksForSummary]);

  // Category counts (today only)
  const categoryCounts = useMemo(() => {
    const source = tasksForSummary;
    return {
      All: source.length,
      Medicine: source.filter((t) => t.category === "Medicine").length,
      Vitals: source.filter((t) => t.category === "Vitals").length,
      Rounds: source.filter((t) => t.category === "Rounds").length,
      Labs: source.filter((t) => t.category === "Labs").length,
      "Discharge Advice": source.filter((t) => t.category === "Discharge Advice").length,
    };
  }, [tasksForSummary]);

  const formatTime = (timeString) => {
    if (!timeString) return "";
    if (timeString.includes("T")) {
      const time = timeString.split("T")[1]?.slice(0, 5);
      if (!time) return "";
      const [hours, minutes] = time.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
      return `${displayHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")} ${period}`;
    }
    const [hours, minutes] = timeString.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${period}`;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Medicine":
        return <MedicationIcon sx={{ fontSize: 18 }} />;
      case "Vitals":
        return <MonitorHeartIcon sx={{ fontSize: 18 }} />;
      case "Labs":
        return <ScienceIcon sx={{ fontSize: 18 }} />;
      case "Discharge Advice":
        return <FileOpenIcon sx={{ fontSize: 18 }} />;
      default:
        return <RefreshIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getShift = () => {
    const hour = new Date().getHours();
    if (hour >= 7 && hour < 15) return "Morning Shift (7AM - 3PM)";
    if (hour >= 15 && hour < 23) return "Evening Shift (3PM - 11PM)";
    return "Night Shift (11PM - 7AM)";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  // Sort dates
  const sortedDates = Object.keys(tasksByDate).sort((a, b) => {
    return new Date(a) - new Date(b);
  });

  return (
    <Container>
      {/* Header */}
      <HeaderCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PersonIcon sx={{ color: "#4caf50", fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Nurse Sarah
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getShift()}
            </Typography>
          </Box>
        </Box>
        <IconButton>
          <Badge badgeContent={6} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </HeaderCard>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard color="#4caf50">
            <CheckCircleIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {summary.done}
            </Typography>
            <Typography variant="body2">Done</Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard color="#81c784">
            <ScheduleIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {summary.pending}
            </Typography>
            <Typography variant="body2">Pending</Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard color="#f44336">
            <WarningIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {summary.overdue}
            </Typography>
            <Typography variant="body2">Overdue</Typography>
          </SummaryCard>
        </Grid>
      </Grid>

      {/* Filter by Category + fixed today badge */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Filter by Category
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {["All", "Medicine", "Vitals", "Rounds", "Labs", "Discharge Advice"].map((category) => (
              <FilterButton
                key={category}
                active={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
                startIcon={
                  category === "All" ? (
                    <GridViewIcon />
                  ) : (
                    getCategoryIcon(category)
                  )
                }
              >
                {category} {categoryCounts[category] || 0}
              </FilterButton>
            ))}
          </Box>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              border: "1px solid #ccc",
              borderRadius: "20px",
              padding: "6px 12px",
              gap: "6px",
              width: 140,
              minWidth: 140,
              boxSizing: "border-box",
              color: "#1c2c4c",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#fff",
            }}
          >
            <FaCalendarAlt style={{ fontSize: "14px", flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              {new Date(selectedDateStr || new Date()).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })}
            </span>
            <IoMdArrowDropdown style={{ fontSize: "16px", flexShrink: 0 }} />
            <input
              type="date"
              value={selectedDateStr}
              onChange={(e) => setSelectedDateStr(e.target.value || "")}
              aria-label="Filter nurse tasks by date"
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                cursor: "pointer",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Today's Tasks */}
      <Paper sx={{ p: 2.5, borderRadius: 12 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Today's Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tasksForSummary.filter((t) => t.status !== "Completed").length}{" "}
            remaining
          </Typography>
        </Box>

        {loading ? (
          <Typography>Loading tasks...</Typography>
        ) : sortedDates.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            No tasks found
          </Typography>
        ) : (
          <Box>
            {sortedDates.map((date) => (
              <Box key={date} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {formatDate(date)}
                </Typography>
                {tasksByDate[date].map((task) => {
                  const isOverdue =
                    task.status !== "Completed" &&
                    (() => {
                      const taskTime =
                        task.dueTime || task.scheduledDateTime?.split("T")[1]?.slice(0, 5);
                      if (!taskTime) return false;
                      const now = new Date();
                      const [hours, minutes] = taskTime.split(":").map(Number);
                      const taskDateTime = new Date();
                      taskDateTime.setHours(hours, minutes, 0, 0);
                      return now > taskDateTime;
                    })();

                  return (
                    <TaskCard
                      key={task.id}
                      urgent={isOverdue || task.priority === "High"}
                      completed={task.status === "Completed"}
                    >
                      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  textDecoration:
                                    task.status === "Completed" ? "line-through" : "none",
                                }}
                              >
                                {task.patientName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  textDecoration:
                                    task.status === "Completed" ? "line-through" : "none",
                                }}
                              >
                                Room {task.roomNo}
                              </Typography>
                            </Box>
                            <Chip
                              icon={getCategoryIcon(task.category)}
                              label={task.category}
                              size="small"
                              sx={{
                                backgroundColor: "#e3f2fd",
                                color: "#1976d2",
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              mb: 1.5,
                              textDecoration:
                                task.status === "Completed" ? "line-through" : "none",
                            }}
                          >
                            {task.taskTitle && (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {task.taskTitle}
                              </Typography>
                            )}
                            {task.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: task.taskTitle ? 0.5 : 0, whiteSpace: "pre-line" }}
                              >
                                {task.description}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(task.dueTime || task.scheduledDateTime)}
                              </Typography>
                            </Box>
                            {(isOverdue || task.priority === "High") && (
                              <Chip
                                label={isOverdue ? "Overdue" : "Urgent"}
                                size="small"
                                sx={{
                                  backgroundColor: "#ff9800",
                                  color: "#fff",
                                  fontSize: "0.7rem",
                                }}
                              />
                            )}
                            {task.status === "Completed" && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Completed"
                                size="small"
                                color="success"
                                sx={{ fontSize: "0.7rem" }}
                              />
                            )}
                          </Box>
                        </Box>
                        {task.category === "Discharge Advice" ? (
                          <Chip
                            label={getDischargeStageLabel(task)}
                            size="small"
                            sx={{
                              backgroundColor: "#ede7f6",
                              color: "#5e35b1",
                              fontWeight: 500,
                            }}
                          />
                        ) : (
                          <Button
                            variant={task.status === "Completed" ? "outlined" : "contained"}
                            size="small"
                            onClick={() => handleCompleteTask(task)}
                            startIcon={
                              task.status === "Completed" ? <CheckCircleIcon /> : null
                            }
                            sx={
                              task.status === "Completed"
                                ? {}
                                : {
                                  backgroundColor: "#1976d2",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: "#1565c0" },
                                }
                            }
                          >
                            {task.status === "Completed" ? "Completed" : "Complete"}
                          </Button>
                        )}
                      </Box>
                    </TaskCard>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NurseTasks;

